import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Achievement } from '@/lib/surf-progression';

interface AchievementRowProps {
  achievement: Achievement;
  completed: boolean;
  onToggle: () => void;
}

const AchievementRow = ({ achievement, completed, onToggle }: AchievementRowProps) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-border/40 last:border-b-0">
      <div className="flex items-start gap-3 py-3">
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={completed}
          aria-label={completed ? `Mark ${achievement.name} as not completed` : `Mark ${achievement.name} as completed`}
          className={cn(
            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
            completed
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-muted-foreground/40 text-transparent hover:border-primary/60'
          )}
        >
          <Check className="h-3.5 w-3.5" />
        </button>

        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex flex-1 items-center justify-between gap-2 text-left"
          aria-expanded={expanded}
        >
          <span className={cn('text-sm sm:text-base', completed ? 'text-foreground' : 'text-foreground/90')}>
            {achievement.name}
            {achievement.optional && (
              <span className="ml-2 text-[11px] uppercase tracking-wide text-muted-foreground">Optional</span>
            )}
          </span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-180')}
          />
        </button>
      </div>

      {expanded && (
        <div className="pb-4 pl-8 pr-2 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200 motion-reduce:animate-none">
          <p className="text-sm text-muted-foreground">{achievement.definition}</p>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {achievement.category === 'Safety & Etiquette' ? 'How to work on it' : 'Best wave to improve this'}
            </p>
            <p className="text-sm text-foreground">{achievement.bestEnvironment}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">What to work on</p>
            <ul className="text-sm text-foreground space-y-1 list-disc list-inside">
              {achievement.whatToWorkOn.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementRow;
